import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  TrendingUp, TrendingDown, DollarSign, Users, Target, BarChart3,
  PieChart, Activity, ArrowUpRight, ArrowDownRight, Building2,
  Briefcase, Globe, Clock, RefreshCw, Loader2, Calendar, CheckCircle2,
  AlertTriangle, Shield, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ title, value, change, changeType, icon: Icon, subtitle, color = "blue" }) => {
  const isPositive = changeType === 'positive';
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {change && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                <span>{change}</span>
                {subtitle && <span className="text-muted-foreground ml-1">{subtitle}</span>}
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, value, max, color = "blue" }) => {
  const percentage = max > 0 ? Math.round((value / max) * 100) : 0;
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500'
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span>{label}</span>
        <span className="font-medium">{percentage}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const BoardDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('quarter');
  const [data, setData] = useState({
    company: null,
    financial: null,
    departments: null,
    risks: null
  });

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchBoardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch multiple data sources in parallel
      const [companyRes, dealsRes, ticketsRes, usersRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/api/reports/company-metrics?period=${period}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/api/deals`, { headers }).catch(() => ({ data: { deals: [] } })),
        axios.get(`${API}/api/tickets`, { headers }).catch(() => ({ data: { tickets: [] } })),
        axios.get(`${API}/api/users/team`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/marketing/campaigns`, { headers }).catch(() => ({ data: { campaigns: [] } }))
      ]);

      // Calculate company metrics
      const deals = dealsRes.data?.deals || dealsRes.data || [];
      const tickets = ticketsRes.data?.tickets || ticketsRes.data || [];
      const users = usersRes.data || [];
      const campaigns = campaignsRes.data?.campaigns || campaignsRes.data || [];

      const totalRevenue = deals
        .filter(d => d.stage === 'closed_won')
        .reduce((sum, d) => sum + (d.value || 0), 0);

      const pipelineValue = deals
        .filter(d => !['closed_won', 'closed_lost'].includes(d.stage))
        .reduce((sum, d) => sum + (d.value || 0), 0);

      const openTickets = tickets.filter(t => t.status !== 'resolved' && t.status !== 'closed').length;
      const activeEmployees = users.length;
      const activeCampaigns = campaigns.filter(c => c.status === 'live' || c.status === 'active').length;

      // Department performance
      const departmentPerformance = [
        { name: 'Sales', target: 1000000, actual: totalRevenue, color: 'blue' },
        { name: 'Marketing', target: 50, actual: activeCampaigns, color: 'purple' },
        { name: 'Support', target: 100, actual: tickets.length - openTickets, color: 'green' },
        { name: 'Operations', target: 95, actual: 92, color: 'orange' }
      ];

      // Risk indicators
      const risks = [
        { 
          level: openTickets > 50 ? 'high' : openTickets > 20 ? 'medium' : 'low',
          area: 'Customer Support',
          description: `${openTickets} unresolved tickets`
        },
        {
          level: pipelineValue < 500000 ? 'high' : pipelineValue < 1000000 ? 'medium' : 'low',
          area: 'Sales Pipeline',
          description: `$${(pipelineValue / 1000).toFixed(0)}K in pipeline`
        }
      ];

      setData({
        company: {
          revenue: totalRevenue,
          pipeline: pipelineValue,
          employees: activeEmployees,
          campaigns: activeCampaigns,
          customerSatisfaction: 94,
          marketShare: 12.5
        },
        financial: {
          quarterlyRevenue: totalRevenue,
          yearOverYear: 15.3,
          profitMargin: 23.5,
          operatingCosts: totalRevenue * 0.65
        },
        departments: departmentPerformance,
        risks
      });

    } catch (err) {
      console.error('Failed to fetch board data:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchBoardData();
  }, [fetchBoardData]);

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="board-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="h-8 w-8 text-indigo-600" />
            Board Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Executive overview for board members and C-suite</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchBoardData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(data.company?.revenue || 0)}
          change="+15.3%"
          changeType="positive"
          subtitle="vs last period"
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Pipeline Value"
          value={formatCurrency(data.company?.pipeline || 0)}
          change="+8.2%"
          changeType="positive"
          subtitle="growth"
          icon={TrendingUp}
          color="blue"
        />
        <StatCard
          title="Active Employees"
          value={data.company?.employees || 0}
          icon={Users}
          color="purple"
        />
        <StatCard
          title="Active Campaigns"
          value={data.company?.campaigns || 0}
          icon={Target}
          color="orange"
        />
        <StatCard
          title="Customer Satisfaction"
          value={`${data.company?.customerSatisfaction || 0}%`}
          change="+2.1%"
          changeType="positive"
          icon={CheckCircle2}
          color="green"
        />
        <StatCard
          title="Market Share"
          value={`${data.company?.marketShare || 0}%`}
          change="+0.8%"
          changeType="positive"
          icon={PieChart}
          color="blue"
        />
      </div>

      {/* Financial Overview & Department Performance */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Financial Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Financial Overview
            </CardTitle>
            <CardDescription>Key financial metrics and trends</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Quarterly Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(data.financial?.quarterlyRevenue || 0)}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground">YoY Growth</p>
                <p className="text-2xl font-bold text-green-600">+{data.financial?.yearOverYear || 0}%</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Profit Margin</p>
                <p className="text-2xl font-bold">{data.financial?.profitMargin || 0}%</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Operating Costs</p>
                <p className="text-2xl font-bold">{formatCurrency(data.financial?.operatingCosts || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Department Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Department Performance
            </CardTitle>
            <CardDescription>Progress against quarterly targets</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(data.departments || []).map((dept, i) => (
              <ProgressBar
                key={i}
                label={dept.name}
                value={dept.actual}
                max={dept.target}
                color={dept.color}
              />
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Risk Assessment & Strategic Initiatives */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Risk Assessment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Risk Assessment
            </CardTitle>
            <CardDescription>Current organizational risks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(data.risks || []).map((risk, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <p className="font-medium">{risk.area}</p>
                    <p className="text-sm text-muted-foreground">{risk.description}</p>
                  </div>
                  <Badge 
                    variant={risk.level === 'high' ? 'destructive' : risk.level === 'medium' ? 'secondary' : 'outline'}
                    className="capitalize"
                  >
                    {risk.level}
                  </Badge>
                </div>
              ))}
              {(!data.risks || data.risks.length === 0) && (
                <p className="text-center text-muted-foreground py-4">No significant risks identified</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Strategic Initiatives */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-purple-500" />
              Strategic Initiatives
            </CardTitle>
            <CardDescription>Key projects and milestones</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { name: 'Digital Transformation', progress: 75, status: 'on_track' },
                { name: 'Market Expansion', progress: 45, status: 'on_track' },
                { name: 'Product Launch Q2', progress: 90, status: 'ahead' },
                { name: 'Cost Optimization', progress: 60, status: 'at_risk' }
              ].map((initiative, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{initiative.name}</span>
                    <Badge 
                      variant={initiative.status === 'ahead' ? 'default' : initiative.status === 'at_risk' ? 'destructive' : 'secondary'}
                      className="capitalize"
                    >
                      {initiative.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${
                        initiative.status === 'ahead' ? 'bg-green-500' : 
                        initiative.status === 'at_risk' ? 'bg-red-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${initiative.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline">
              <BarChart3 className="h-4 w-4 mr-2" />
              View Full Financial Report
            </Button>
            <Button variant="outline">
              <Users className="h-4 w-4 mr-2" />
              Team Performance Report
            </Button>
            <Button variant="outline">
              <Globe className="h-4 w-4 mr-2" />
              Market Analysis
            </Button>
            <Button variant="outline">
              <Calendar className="h-4 w-4 mr-2" />
              Schedule Board Meeting
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BoardDashboardPage;
